'use client';

import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { localizeUrl, pickButton, stripHtml } from '@/lib/homepage-mappers';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { CommunityPreFooter } from '@/types/community';
import { Reveal } from '../lib/community-reveal';

interface CommunityPreFooterProps {
  locale: string;
  zone: CommunityPreFooter;
}

export default function CommunityPreFooterSection({ locale, zone }: CommunityPreFooterProps) {
  const primaryButton = pickButton(zone.button, 'Solid') ?? zone.button?.[0];
  const secondaryButton =
    zone.button?.find((b) => b.buttonType === 'Transparent') ?? zone.button?.[1];

  return (
    <section className="relative overflow-hidden bg-[#ed1c22] px-4 py-10 sm:px-6 sm:py-12 md:px-16 md:py-14 lg:px-24">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border border-white/10" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full border border-white/10" />
      <Reveal direction="up">
        <div className="container relative z-10 mx-auto text-center">
          {zone.heading ? (
            <h2 className="text-[1.75rem] font-bold leading-tight text-white sm:text-3xl md:text-4xl">
              {zone.heading}
            </h2>
          ) : null}
          {zone.subHeading ? (
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/85 sm:text-[15px] sm:leading-8">
              {stripHtml(zone.subHeading)}
            </p>
          ) : null}
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            {primaryButton?.buttonText?.trim() ? (
              <SwapTextButton
                text={primaryButton.buttonText.trim()}
                hoverText={primaryButton.buttonText.trim()}
                aria-label={primaryButton.ariaLabel?.trim() || primaryButton.buttonText.trim()}
                href={localizeUrl(primaryButton.buttonUrl, locale) ?? undefined}
                {...getCmsButtonLinkProps(primaryButton)}
                tone="light"
                className="!rounded-full !px-7 !py-3"
              />
            ) : null}
            {secondaryButton?.buttonText?.trim() ? (
              <SwapTextButton
                text={secondaryButton.buttonText.trim()}
                hoverText={secondaryButton.buttonText.trim()}
                aria-label={secondaryButton.ariaLabel?.trim() || secondaryButton.buttonText.trim()}
                href={localizeUrl(secondaryButton.buttonUrl, locale) ?? undefined}
                {...getCmsButtonLinkProps(secondaryButton)}
                variant="outline"
                className="!rounded-full !border-white/40 !bg-transparent !px-7 !py-3 !text-white hover:!bg-white hover:!text-[#ed1c22]"
              />
            ) : null}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
